import type { FindingCounts } from '@nirvik/shared';
import { useCssVars } from '@/hooks/use-css-vars';

const severityConfig: { key: keyof Omit<FindingCounts, 'total' | 'info'>; label: string; color: string; bgColor: string }[] = [
  { key: 'critical', label: 'Critical', color: 'bg-red-500', bgColor: 'bg-red-50' },
  { key: 'high', label: 'High', color: 'bg-orange-500', bgColor: 'bg-orange-50' },
  { key: 'medium', label: 'Medium', color: 'bg-yellow-500', bgColor: 'bg-yellow-50' },
  { key: 'low', label: 'Low', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
];

interface FindingSummaryChartProps {
  counts: FindingCounts;
}

export function FindingSummaryChart({ counts }: FindingSummaryChartProps) {
  const maxCount = Math.max(counts.critical, counts.high, counts.medium, counts.low, 1);

  return (
    <div className="space-y-3">
      {severityConfig.map(({ key, label, color }) => {
        const count = counts[key];
        const widthPercent = (count / maxCount) * 100;
        const renderedWidth = count > 0 ? Math.max(widthPercent, 4) : 0;
        return (
          <SeverityRow key={key} label={label} color={color} count={count} widthPercent={renderedWidth} />
        );
      })}
    </div>
  );
}

interface SeverityRowProps {
  label: string;
  color: string;
  count: number;
  widthPercent: number;
}

function SeverityRow({ label, color, count, widthPercent }: SeverityRowProps) {
  // Bar width is dynamic per render; use a CSS custom variable on the bar
  // so we never set an inline `style` attribute (CSP style-src-attr is strict).
  const barRef = useCssVars<HTMLDivElement>({ '--bar-width': `${widthPercent}%` });
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground dark:text-muted-foreground w-14 text-right shrink-0">{label}</span>
      <div className="flex-1 h-8 bg-gray-100 dark:bg-muted rounded-full overflow-hidden">
        <div
          ref={barRef}
          className={`h-full ${color} rounded-full transition-all duration-500 ease-out w-[var(--bar-width)]`}
        />
      </div>
      <span className="text-sm font-semibold text-gray-700 dark:text-foreground w-8 text-right shrink-0">
        {count}
      </span>
    </div>
  );
}
